import {
  requireWorkflowPayloadVersion,
  type WorkflowAutomatedStep,
  type WorkflowAutomatedInput,
  type WorkflowDefinitionDocument,
} from './platform-workflow-definition-lifecycle.models';
import {
  automationLiteralError,
  type AutomationField,
  type WorkflowAutomationCatalog,
} from './workflow-automation-authoring';
import {
  capabilityIdentity,
  capabilityReferencesShapeError,
  sameCapabilityReference,
} from './workflow-capability-authoring';

export interface AutomatedOutcomeEdit {
  commandOutcome: string;
  routeOutcome: string;
  destinationNodeId: string;
}

const jsonNumber = /^(-?)(0|[1-9]\d*)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

/** Validate exact text without replacing the binding with a rounded JavaScript number. */
export function automatedLiteralError(
  field: AutomationField,
  input: WorkflowAutomatedInput,
): string | null {
  if (input.literalJson == null)
    return automationLiteralError(field, input.value);
  if (
    typeof input.literalJson !== 'string' ||
    input.value != null ||
    input.context != null
  )
    return 'Use one literal value source.';
  const text = input.literalJson.trim();
  if (text === 'null') return field.nullable ? null : 'A value is required.';
  if (field.type === 'number')
    return jsonNumber.test(text)
      ? null
      : 'Enter a JSON number, using a decimal point when needed.';
  if (field.type === 'integer') {
    const match = jsonNumber.exec(text);
    if (!match) return 'Enter a whole number in the supported range.';
    const digits = match[2] + (match[3] ?? '');
    let value = BigInt(digits);
    if (value === 0n) return null;
    const scale = BigInt(match[3]?.length ?? 0) - BigInt(match[4] ?? '0');
    if (scale > BigInt(digits.length) || scale < -10n)
      return 'Enter a whole number in the supported range.';
    if (scale > 0n) {
      const divisor = 10n ** scale;
      if (value % divisor !== 0n)
        return 'Enter a whole number in the supported range.';
      value /= divisor;
    } else value *= 10n ** -scale;
    if (match[1]) value = -value;
    return value >= -2147483648n && value <= 2147483647n
      ? null
      : 'Enter a whole number in the supported range.';
  }
  try {
    // Parsing checks only the declared shape. Keep the original literalJson
    // for transport, even where a numeric leaf exceeds JavaScript precision.
    return automationLiteralError(field, JSON.parse(text));
  } catch {
    return 'Enter valid JSON matching the declared input.';
  }
}
export function automatedStepFingerprint(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): string {
  return JSON.stringify([
    document.workflowCode,
    document.packReference,
    document.capabilityReferences,
    document.nodes.map((node) => ({
      id: node.id,
      typeCode: node.typeCode,
      stepCode: node.stepCode,
      ...(node.id === nodeId ? { automation: node.automation } : {}),
    })),
    document.edges,
    nodeId,
  ]);
}
export function automatedInputContextCompatible(
  field: AutomationField,
  context: WorkflowAutomationCatalog['contextBindings'][number],
  document: WorkflowDefinitionDocument,
): boolean {
  return (
    context.triggerKinds.includes('entry') &&
    (context.code !== 'subject.id' || !!document.subjectTypeCode) &&
    context.type === field.type &&
    (!context.nullable || field.nullable) &&
    !field.values?.length &&
    (field.maxLength == null ||
      (context.maxLength != null && context.maxLength <= field.maxLength))
  );
}
/** Installed metadata validates bindings; the server remains compiler/routing/execution authority. */
export function applyAutomatedStep(
  document: WorkflowDefinitionDocument,
  nodeId: string,
  automation: WorkflowAutomatedStep,
  outcomes: readonly AutomatedOutcomeEdit[],
  catalog: WorkflowAutomationCatalog,
): WorkflowDefinitionDocument {
  const nodes = document.nodes.filter((node) => node.id === nodeId);
  if (nodes.length !== 1 || nodes[0].typeCode !== 'automated_step')
    throw new Error('Select one existing automated step.');
  const commandIdentity = automation.command?.key && capabilityIdentity(automation.command.key);
  const operations = Array.isArray(catalog.operations) && commandIdentity
    ? catalog.operations.filter((item) => item?.command?.key && capabilityIdentity(item.command.key) === commandIdentity)
    : [];
  const operation = operations.length === 1 && !capabilityReferencesShapeError([operations[0].command])
    && !capabilityReferencesShapeError([automation.command]) && sameCapabilityReference(operations[0].command, automation.command)
    ? operations[0] : null;
  if (catalog.contractVersion !== 1 || !operation?.availableForNewUse || !operation.packPermitted || operation.lifecycle !== 'available')
    throw new Error(
      'Select an available exact installed activity for this workflow and pack. Retained settings are preserved.',
    );
  if (automation.version !== 1)
    throw new Error(
      'The retained automated-step contract version is unavailable.',
    );
  if (
    automation.inputs.length !==
      new Set(automation.inputs.map((input) => input.field)).size ||
    automation.inputs.some(
      (input) =>
        !operation.input.fields.some((field) => field.name === input.field),
    )
  )
    throw new Error('Use each declared input at most once.');
  for (const field of operation.input.fields) {
    const input = automation.inputs.find((item) => item.field === field.name);
    if (!input) {
      if (field.required) throw new Error(`Supply ${field.name}.`);
      continue;
    }
    if (!operation.automationInputs.includes(field.name))
      throw new Error(
        `${field.name}: this input is not declared for automatic use.`,
      );
    if (input.source === 'literal') {
      const error = automatedLiteralError(field, input);
      if (error) throw new Error(`${field.name}: ${error}`);
      if (input.context != null)
        throw new Error(`${field.name}: use one literal value source.`);
    } else if (input.source === 'context') {
      const context = catalog.contextBindings.find(
        (item) => item.code === input.context,
      );
      if (
        input.literalJson != null ||
        input.value != null ||
        !context ||
        !automatedInputContextCompatible(field, context, document)
      )
        throw new Error(`${field.name}: choose a compatible captured context.`);
    } else
      throw new Error(
        `${field.name}: use a literal value or registered captured context.`,
      );
  }
  if (
    outcomes.length !== operation.outcomes.length ||
    outcomes.some(
      (outcome) => !operation.outcomes.includes(outcome.commandOutcome),
    ) ||
    new Set(outcomes.map((outcome) => outcome.commandOutcome)).size !==
      outcomes.length ||
    new Set(outcomes.map((outcome) => outcome.routeOutcome)).size !==
      outcomes.length ||
    outcomes.some(
      (outcome) =>
        !/^[a-z][a-z0-9._-]{0,119}$/.test(outcome.routeOutcome) ||
        outcome.destinationNodeId === nodeId ||
        document.nodes.filter(
          (node) =>
            node.id === outcome.destinationNodeId &&
            [
              'human_step',
              'automated_step',
              'routing_decision',
              'terminal',
            ].includes(node.typeCode),
        ).length !== 1 ||
        document.nodes.filter((node) => node.id === outcome.destinationNodeId).length !== 1,
    )
  )
    throw new Error(
      'Map every business outcome once to a unique route and an explicit existing destination.',
    );
  if (!Array.isArray(operation.requiredCapabilities) || capabilityReferencesShapeError(operation.requiredCapabilities)
    || operation.requiredCapabilities.filter(reference => sameCapabilityReference(reference, operation.command)).length !== 1)
    throw new Error('The installed activity dependency closure is incomplete or ambiguous.');
  if (document.capabilityReferences != null && !Array.isArray(document.capabilityReferences))
    throw new Error('The saved capability pins are unavailable for editing. Their original content is preserved.');
  const capabilityReferences = [...(document.capabilityReferences ?? [])];
  for (const reference of operation.requiredCapabilities) {
    const matches = capabilityReferences.filter(
      (item) =>
        item?.key && capabilityIdentity(item.key) === capabilityIdentity(reference.key),
    );
    if (matches.length > 1 || (matches.length === 1 && capabilityReferencesShapeError(matches)))
      throw new Error('A required capability pin is malformed or duplicated. Repair that pin explicitly.');
    const existing = matches[0];
    if (existing && !sameCapabilityReference(existing, reference))
      throw new Error(
        'An exact capability pin conflicts with this activity dependency. Repair that pin explicitly.',
      );
    if (!existing) capabilityReferences.push(reference);
  }
  const oldEdges = document.edges.filter((edge) => edge.fromNodeId === nodeId);
  if (oldEdges.some(edge => typeof edge.id !== 'string' || !edge.id.trim()
    || document.edges.filter(candidate => candidate.id === edge.id).length !== 1))
    throw new Error('A saved automated path identity is malformed or duplicated. Repair that path explicitly.');
  const priorMappings =
    nodes[0].automation?.outcomes?.map((outcome) => outcome.routeOutcome) ?? [];
  const provisional =
    !nodes[0].automation &&
    oldEdges.length === 1 &&
    oldEdges[0].pathKindCode == null &&
    oldEdges[0].outcomeCode == null
      ? oldEdges[0]
      : undefined;
  if (
    oldEdges.some(
      (edge) =>
        edge !== provisional &&
        (edge.pathKindCode != null ||
          !edge.outcomeCode ||
          !priorMappings.includes(edge.outcomeCode)),
    )
  )
    throw new Error(
      'An existing automated path is outside its declared outcome contract. Repair that path explicitly before applying settings.',
    );
  const replacements = new Map<(typeof oldEdges)[number], (typeof oldEdges)[number]>();
  const additions: typeof oldEdges = [];
  const allocatedIds = new Set(document.edges.map(edge => edge.id));
  const retainedActivity = nodes[0].automation?.command?.key
    && sameCapabilityReference(nodes[0].automation.command, automation.command);
  for (const [index, outcome] of outcomes.entries()) {
    const previous = retainedActivity
      ? nodes[0].automation!.outcomes.filter(mapping => mapping.commandOutcome === outcome.commandOutcome) : [];
    if (previous.length > 1)
      throw new Error('The saved automated path mapping is ambiguous. Repair that path explicitly.');
    // A retained output owns its existing connector even when route keys are renamed or swapped.
    const previousCode = previous.length === 1 ? previous[0].routeOutcome : outcome.routeOutcome;
    const existing = oldEdges.filter(edge => edge.outcomeCode === previousCode);
    if (existing.length > 1)
      throw new Error('Duplicate saved outcome paths need explicit repair.');
    const retained = existing[0] ?? (index === 0 ? provisional : undefined);
    if (retained && replacements.has(retained))
      throw new Error('The saved automated path mapping is ambiguous. Repair that path explicitly.');
    const knownSelectors = {
      fromNodeId: nodeId,
      toNodeId: outcome.destinationNodeId,
      outcomeCode: outcome.routeOutcome,
    };
    if (retained) replacements.set(retained, { ...retained, ...knownSelectors });
    else {
      const stem = `path.${nodeId}.${outcome.routeOutcome}`.slice(0, 108);
      let id = stem, suffix = 2;
      while (allocatedIds.has(id)) id = `${stem}-${suffix++}`;
      allocatedIds.add(id);
      additions.push({ id, ...knownSelectors });
    }
  }
  const edges = document.edges.flatMap(edge => edge.fromNodeId !== nodeId ? [edge]
    : replacements.has(edge) ? [replacements.get(edge)!] : []).concat(additions);
  return {
    ...document,
    payloadSchemaVersion: requireWorkflowPayloadVersion(
      document.payloadSchemaVersion,
      '1.15',
    ),
    capabilityReferences,
    nodes: document.nodes.map((node) =>
      node.id === nodeId
        ? {
            ...node,
            automation: {
              ...structuredClone(automation),
              outcomes: outcomes.map(({ commandOutcome, routeOutcome }) => {
                const retained = automation.outcomes.filter(mapping => mapping.commandOutcome === commandOutcome);
                if (retained.length > 1) throw new Error('The saved automated outcome identity is duplicated. Repair that mapping explicitly.');
                return { ...structuredClone(retained[0] ?? {}), commandOutcome, routeOutcome };
              }),
            },
          }
        : node,
    ),
    edges,
  };
}
